import { useCallback, useEffect, useState } from 'react';

import { notifications } from '@mantine/notifications';

import { clearCachedCandidates } from '@/hooks/subjectCandidatesCache';
import { vnextApiClient } from '@/services/vnext-api';
import {
  PricingUpdateRequest,
  ReportRequestDetail,
  ReportRequestSummary,
  TriageAddSubjectRequest,
  TriageAdvanceRequest,
  TriageEditSubjectRequest,
  TriageInitialUpdateRequest,
  TriageManualResolveRequest,
  TriageRejectRequest,
  TriageRevertRequest,
  TriageStage,
  TriageStartResponse,
} from '@/types/report-request';
import { ApiSubject } from '@/types/screening';
import { notifyIfBackofficeBehind } from '@/utils/backofficeSyncNotice';

interface UseReportRequestsParams {
  page?: number;
  limit?: number;
  triageStage?: TriageStage;
  teamId?: string | number;
  autoFetch?: boolean;
}

export function useReportRequests(params: UseReportRequestsParams = {}) {
  const { page = 1, limit = 25, triageStage, teamId, autoFetch = true } = params;

  const [items, setItems] = useState<ReportRequestSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchList = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await vnextApiClient.listReportRequests({
        page,
        limit,
        triage_stage: triageStage,
        team_id: teamId,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to load report requests');
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, triageStage, teamId]);

  useEffect(() => {
    if (autoFetch) {
      fetchList();
    }
  }, [autoFetch, fetchList]);

  return { items, total, isLoading, error, refetch: fetchList };
}

export function useReportRequestTriage(reportRequestId: string | undefined) {
  const [detail, setDetail] = useState<ReportRequestDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!reportRequestId) {
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const result = await vnextApiClient.getReportRequest(reportRequestId);
      setDetail(result);
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to load report request');
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [reportRequestId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const updateInitial = useCallback(
    async (body: TriageInitialUpdateRequest) => {
      if (!reportRequestId) {
        return null;
      }
      try {
        const result = await vnextApiClient.updateTriageInitial(reportRequestId, body);
        setDetail(result);
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Failed to update');
        notifications.show({ title: 'Update failed', message: e.message, color: 'red' });
        throw e;
      }
    },
    [reportRequestId]
  );

  const updateExtractionSources = useCallback(
    async (attachmentIds: string[] | null) => {
      if (!reportRequestId) {
        return null;
      }
      try {
        const result = await vnextApiClient.updateExtractionSources(reportRequestId, attachmentIds);
        setDetail(result);
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Failed to update extraction sources');
        notifications.show({ title: 'Update failed', message: e.message, color: 'red' });
        throw e;
      }
    },
    [reportRequestId]
  );

  const updateConfig = useCallback(
    async (configId: string) => {
      if (!reportRequestId) {
        return null;
      }
      try {
        const result = await vnextApiClient.updateReportRequestConfig(reportRequestId, configId);
        setDetail(result);
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Failed to set the report config');
        notifications.show({ title: 'Config update failed', message: e.message, color: 'red' });
        throw e;
      }
    },
    [reportRequestId]
  );

  const updatePricing = useCallback(
    async (body: PricingUpdateRequest) => {
      if (!reportRequestId) {
        return null;
      }
      try {
        const result = await vnextApiClient.updatePricing(reportRequestId, body);
        setDetail(result);
        if (!notifyIfBackofficeBehind(result.backofficeSync)) {
          notifications.show({
            title: 'Pricing updated',
            message: 'Price, turnaround, and expedited flag saved.',
            color: 'green',
          });
        }
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Failed to update pricing');
        notifications.show({ title: 'Pricing update failed', message: e.message, color: 'red' });
        throw e;
      }
    },
    [reportRequestId]
  );

  const advance = useCallback(
    async (target: TriageStage) => {
      if (!reportRequestId) {
        return null;
      }
      try {
        const result = await vnextApiClient.advanceTriage(reportRequestId, {
          target,
        } as TriageAdvanceRequest);
        // Entering identification (re)fires the graph: the server just
        // cleared + is recreating this report's candidates, so the
        // process-lifetime cache must not re-paint the old set.
        if (result.reportId && result.triageStage === 'subjects_identified') {
          clearCachedCandidates(result.reportId);
        }
        notifications.show({
          title: 'Stage advanced',
          message: `Now at ${result.triageStage}`,
          color: 'green',
        });
        setDetail(result);
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Failed to advance stage');
        notifications.show({ title: 'Advance failed', message: e.message, color: 'red' });
        throw e;
      }
    },
    [reportRequestId]
  );

  const revert = useCallback(
    async (toStage: TriageStage, reason: string) => {
      if (!reportRequestId) {
        return null;
      }
      try {
        const result = await vnextApiClient.revertTriage(reportRequestId, {
          to_stage: toStage,
          reason,
        } as TriageRevertRequest);
        // Landing at subjects_extracted or earlier deletes the report's
        // candidates server-side — drop the cached set so the
        // identification panel can't flash deleted subjects back.
        if (
          result.reportId &&
          (result.triageStage === 'initial' || result.triageStage === 'subjects_extracted')
        ) {
          clearCachedCandidates(result.reportId);
        }
        notifications.show({
          title: 'Reverted',
          message: `Now at ${result.triageStage}`,
          color: 'gray',
        });
        setDetail(result);
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Failed to revert stage');
        notifications.show({ title: 'Revert failed', message: e.message, color: 'red' });
        throw e;
      }
    },
    [reportRequestId]
  );

  const reject = useCallback(
    async (reason: string) => {
      if (!reportRequestId) {
        return null;
      }
      try {
        const result = await vnextApiClient.rejectReportRequest(reportRequestId, {
          reason,
        } as TriageRejectRequest);
        notifications.show({
          title: 'Rejected',
          message: 'ReportRequest has been rejected.',
          color: 'gray',
        });
        setDetail(result);
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Failed to reject');
        notifications.show({ title: 'Reject failed', message: e.message, color: 'red' });
        throw e;
      }
    },
    [reportRequestId]
  );

  const splitRequest = useCallback(async () => {
    if (!reportRequestId) {
      return null;
    }
    try {
      const result = await vnextApiClient.splitReportRequest(reportRequestId);
      const codenames = result.created
        .map((rr) => rr.codename)
        .filter(Boolean)
        .join(', ');
      notifications.show({
        title: `Split into ${result.created.length + 1} requests`,
        message: codenames
          ? `New requests: ${codenames}. This request keeps the first subject.`
          : 'This request keeps the first subject.',
        color: 'green',
      });
      await fetchDetail();
      return result;
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to split the request');
      notifications.show({ title: 'Split failed', message: e.message, color: 'red' });
      throw e;
    }
  }, [reportRequestId, fetchDetail]);

  const addSubject = useCallback(
    async (body: TriageAddSubjectRequest): Promise<ApiSubject | null> => {
      if (!reportRequestId) {
        return null;
      }
      try {
        const result = await vnextApiClient.addTriageSubject(reportRequestId, body);
        await fetchDetail();
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Failed to add subject');
        notifications.show({ title: 'Add failed', message: e.message, color: 'red' });
        throw e;
      }
    },
    [reportRequestId, fetchDetail]
  );

  const editSubject = useCallback(
    async (subjectId: string, body: TriageEditSubjectRequest): Promise<ApiSubject | null> => {
      if (!reportRequestId) {
        return null;
      }
      try {
        const result = await vnextApiClient.editTriageSubject(reportRequestId, subjectId, body);
        await fetchDetail();
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Failed to edit subject');
        notifications.show({ title: 'Edit failed', message: e.message, color: 'red' });
        throw e;
      }
    },
    [reportRequestId, fetchDetail]
  );

  const removeSubject = useCallback(
    async (subjectId: string): Promise<void> => {
      if (!reportRequestId) {
        return;
      }
      try {
        await vnextApiClient.removeTriageSubject(reportRequestId, subjectId);
        await fetchDetail();
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Failed to remove subject');
        notifications.show({ title: 'Remove failed', message: e.message, color: 'red' });
        throw e;
      }
    },
    [reportRequestId, fetchDetail]
  );

  const reExtractSubject = useCallback(
    async (subjectId: string, additionalContext?: string): Promise<ApiSubject | null> => {
      if (!reportRequestId) {
        return null;
      }
      try {
        const result = await vnextApiClient.reExtractTriageSubject(
          reportRequestId,
          subjectId,
          additionalContext
        );
        notifications.show({
          title: 'Re-extracted',
          message: additionalContext
            ? 'Subject fields refreshed using your added context.'
            : 'Subject fields refreshed from the source string.',
          color: 'gray',
        });
        await fetchDetail();
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Failed to re-extract subject');
        notifications.show({ title: 'Re-extract failed', message: e.message, color: 'red' });
        throw e;
      }
    },
    [reportRequestId, fetchDetail]
  );

  const selectCandidate = useCallback(
    async (subjectId: string, candidateId: string) => {
      if (!reportRequestId) {
        return null;
      }
      try {
        const result = await vnextApiClient.selectTriageCandidate(
          reportRequestId,
          subjectId,
          candidateId
        );
        setDetail(result);
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Failed to select candidate');
        notifications.show({ title: 'Selection failed', message: e.message, color: 'red' });
        throw e;
      }
    },
    [reportRequestId]
  );

  const clarifySubject = useCallback(
    async (subjectId: string, additionalContext: string) => {
      if (!reportRequestId) {
        return null;
      }
      try {
        const result = await vnextApiClient.clarifyTriageSubject(
          reportRequestId,
          subjectId,
          additionalContext
        );
        notifications.show({
          title: 'Clarification submitted',
          message: 'Re-running identification with additional context.',
          color: 'gray',
        });
        setDetail(result);
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Failed to clarify');
        notifications.show({ title: 'Clarify failed', message: e.message, color: 'red' });
        throw e;
      }
    },
    [reportRequestId]
  );

  const resolveSubjectManually = useCallback(
    async (subjectId: string, body: TriageManualResolveRequest) => {
      if (!reportRequestId) {
        return null;
      }
      try {
        const result = await vnextApiClient.resolveTriageSubjectManually(
          reportRequestId,
          subjectId,
          body
        );
        notifications.show({
          title: 'Subject created',
          message: 'Subject recorded manually and marked resolved.',
          color: 'green',
        });
        setDetail(result);
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Failed to resolve subject');
        notifications.show({ title: 'Manual resolve failed', message: e.message, color: 'red' });
        throw e;
      }
    },
    [reportRequestId]
  );

  const start = useCallback(async (): Promise<TriageStartResponse | null> => {
    if (!reportRequestId) {
      return null;
    }
    try {
      const result = await vnextApiClient.startReportRequest(reportRequestId);
      notifications.show({
        title: 'Report started',
        message: 'Report has been queued for processing.',
        color: 'green',
      });
      await fetchDetail();
      return result;
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to start report');
      notifications.show({ title: 'Start failed', message: e.message, color: 'red' });
      throw e;
    }
  }, [reportRequestId, fetchDetail]);

  return {
    detail,
    isLoading,
    error,
    refetch: fetchDetail,
    updateInitial,
    updateExtractionSources,
    updateConfig,
    updatePricing,
    advance,
    revert,
    reject,
    start,
    splitRequest,
    addSubject,
    editSubject,
    removeSubject,
    reExtractSubject,
    selectCandidate,
    clarifySubject,
    resolveSubjectManually,
  };
}
